import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with credentials
const api = axios.create({
    baseURL: API,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Auth API
export const authAPI = {
    exchangeSession: (sessionId) => 
        api.post('/auth/session', { session_id: sessionId }),
    
    getMe: () => api.get('/auth/me'),
    
    logout: () => api.post('/auth/logout'),
};

// Users API
export const usersAPI = {
    getUser: (userId) => api.get(`/users/${userId}`),
    
    updateMe: (data) => api.put('/users/me', data),
};

// Games API
export const gamesAPI = {
    getGames: (params) => api.get('/games', { params }),
    
    getGame: (gameId) => api.get(`/games/${gameId}`),
    
    createGame: (data) => api.post('/games', data),
    
    joinGame: (gameId) => api.post(`/games/${gameId}/join`),
    
    leaveGame: (gameId) => api.post(`/games/${gameId}/leave`),
    
    cancelGame: (gameId) => api.delete(`/games/${gameId}`),
};

// Characters API (D&D)
export const charactersAPI = {
    getCharacters: () => api.get('/characters'),
    
    createCharacter: (data) => api.post('/characters', data),
    
    updateCharacter: (sheetId, data) => api.put(`/characters/${sheetId}`, data),
    
    deleteCharacter: (sheetId) => api.delete(`/characters/${sheetId}`),
    
    importFromPDF: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/characters/import/pdf', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
};

// DM Requests API (Find a DM)
export const dmRequestsAPI = {
    getRequests: (params = {}) => api.get('/dnd/dm-requests', { params }),
    
    getMyRequests: () => api.get('/dnd/dm-requests/my'),
    
    getRequest: (requestId) => api.get(`/dnd/dm-requests/${requestId}`),
    
    createRequest: (data) => api.post('/dnd/dm-requests', data),
    
    respondToRequest: (requestId, data) => api.post(`/dnd/dm-requests/${requestId}/respond`, data),
    
    acceptResponse: (requestId, dmId) => api.post(`/dnd/dm-requests/${requestId}/accept/${dmId}`),
    
    closeRequest: (requestId) => api.post(`/dnd/dm-requests/${requestId}/close`),
    
    deleteRequest: (requestId) => api.delete(`/dnd/dm-requests/${requestId}`),
};

// MTG API
export const mtgAPI = {
    searchCards: (q, page = 1) => 
        api.get('/mtg/search', { params: { q, page } }),
    
    searchCommanders: (q = '', colors = '') => 
        api.get('/mtg/commanders', { params: { q, colors } }),
    
    getCard: (cardId) => api.get(`/mtg/card/${cardId}`),
    
    getCardByName: (name, exact = false) =>
        api.get('/mtg/card/named', { params: exact ? { exact: name } : { fuzzy: name } }),
    
    autocompleteCard: (q) =>
        api.get('/mtg/card/autocomplete', { params: { q } }),
    
    getCardCollection: (identifiers) =>
        api.post('/mtg/card/collection', { identifiers }),
    
    getRandomCard: (q = '') => api.get('/mtg/random', { params: { q } }),
    
    // Deck management
    getDecks: () => api.get('/mtg/decks'),
    
    getDeck: (deckId) => api.get(`/mtg/decks/${deckId}`),
    
    createDeck: (data) => api.post('/mtg/decks', data),
    
    importDeck: (data) => api.post('/mtg/decks/import', data),
    
    updateDeckCards: (deckId, cards) => api.put(`/mtg/decks/${deckId}/cards`, cards),
    
    deleteDeck: (deckId) => api.delete(`/mtg/decks/${deckId}`),
};

// LGS API
export const lgsAPI = {
    getLGSList: (params) => api.get('/lgs', { params }),
    
    getLGSGames: (userId) => api.get(`/lgs/${userId}/games`),
};

// Geocoding API
export const geocodeAPI = {
    searchAddresses: (q, limit = 5) => 
        api.get('/geocode/search', { params: { q, limit } }),
    
    reverseGeocode: (lat, lon) => 
        api.get('/geocode/reverse', { params: { lat, lon } }),
};

// Messages API
export const messagesAPI = {
    getMessages: (gameId, params = {}) => 
        api.get(`/games/${gameId}/messages`, { params }),
    
    sendMessage: (gameId, content) => 
        api.post(`/games/${gameId}/messages`, { content }),
    
    markAsRead: (gameId) =>
        api.post(`/games/${gameId}/messages/read`),
    
    getUnreadCounts: () =>
        api.get('/games/unread-counts'),
};

// User API (extended)
export const userAPI = {
    getMyGames: (params = {}) => 
        api.get('/users/me/games', { params }),
    
    getMyHistory: (params = {}) =>
        api.get('/users/me/history', { params }),
    
    getMyStats: () =>
        api.get('/users/me/stats'),
    
    getMyReminders: () =>
        api.get('/users/me/reminders'),
};

// Reminders API
export const remindersAPI = {
    createReminder: (gameId, hoursBefore = 1) =>
        api.post(`/games/${gameId}/reminders`, null, { params: { hours_before: hoursBefore } }),
    
    deleteReminder: (reminderId) =>
        api.delete(`/reminders/${reminderId}`),
};

// 4th Dimension (Game Room) API
export const fourthDimensionAPI = {
    getInfo: () => api.get('/fourth_dimension/info'),
    
    getAvailability: (params = {}) => api.get('/fourth_dimension/availability', { params }),
    
    getDateAvailability: (month, year) => api.get('/fourth_dimension/availability/dates', { params: { month, year } }),
    
    createBooking: (data) => api.post('/fourth_dimension/bookings', data),
    
    // Admin endpoints
    getBookings: (params = {}) => api.get('/fourth_dimension/bookings', { params }),
    
    updateBookingStatus: (bookingId, status) => api.put(`/fourth_dimension/bookings/${bookingId}/status`, null, { params: { status } }),
    
    createSlots: (slots) => api.post('/fourth_dimension/admin/availability', slots),
    
    blockDates: (dates, isBlocked) => api.post('/fourth_dimension/admin/block-dates', { dates, is_blocked: isBlocked }),
    
    deleteSlot: (slotId) => api.delete(`/fourth_dimension/admin/availability/${slotId}`),
    
    generateSlots: (startDate, endDate, timeSlots) => 
        api.post('/fourth_dimension/admin/generate-slots', null, { 
            params: { start_date: startDate, end_date: endDate, time_slots: JSON.stringify(timeSlots) }
        }),
    
    checkAdmin: () => api.get('/fourth_dimension/admin/check'),
};

export default api;
